/**
 * VideoQuiz.tsx
 * Builds an interactive Video Quiz activity: a YouTube video (or uploaded file)
 * with timestamped questions. Gemini can generate questions from a YouTube URL;
 * the teacher can also add/edit them. During a live session the teacher taps each
 * question at the right moment (see VideoPresenter) to push it to students.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Plus, Trash2, Check, Youtube, Upload, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import { generateVideoQuestions } from '@ai/geminiClient';
import type { VideoQuizConfig, VideoQuestion, MCOption } from '@types-local/quiz.types';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
function newId(p: string) { return `${p}_${Math.random().toString(36).slice(2, 9)}`; }
function blankMC(): VideoQuestion {
  return {
    id: newId('vq'), timestamp: 0, kind: 'mc', question: '', starValue: 1, blooms: null,
    options: [0, 1, 2, 3].map((i) => ({ id: newId('opt'), text: '', isCorrect: i === 0 })),
  };
}

export default function VideoQuiz(): React.ReactElement {
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  const [source, setSource] = useState<'youtube' | 'upload'>('youtube');
  const [videoUrl, setVideoUrl] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [questions, setQuestions] = useState<VideoQuestion[]>([]);
  const [genCount, setGenCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Repopulate from the current slide's saved config.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const cfg = loadSlideConfig(await getCurrentSlideIndex());
      if (cancelled || cfg?.type !== 'video_quiz') return;
      setSource(cfg.source);
      setVideoUrl(cfg.videoUrl ?? '');
      setQuestions(cfg.questions ?? []);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const handleGenerate = async () => {
    setError('');
    if (source !== 'youtube' || !videoUrl.trim()) {
      setError('Paste a YouTube link to generate questions with AI.');
      return;
    }
    setGenerating(true);
    try {
      const qs = await generateVideoQuestions(videoUrl.trim(), genCount);
      if (qs.length === 0) throw new Error('No questions were generated. Try a different video.');
      setQuestions(qs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const updateQ = (id: string, patch: Partial<VideoQuestion>) =>
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  const updateOpt = (qid: string, oid: string, text: string) =>
    setQuestions((prev) => prev.map((q) => q.id === qid
      ? { ...q, options: q.options?.map((o) => (o.id === oid ? { ...o, text } : o)) } : q));
  const setCorrect = (qid: string, oid: string) =>
    setQuestions((prev) => prev.map((q) => q.id === qid
      ? { ...q, options: q.options?.map((o) => ({ ...o, isCorrect: o.id === oid })) } : q));
  const removeQ = (id: string) => setQuestions((prev) => prev.filter((q) => q.id !== id));

  const canSave = videoUrl.trim().length > 0 && questions.length > 0
    && questions.every((q) => q.question.trim());

  const handleSave = async () => {
    if (!canSave) return;
    setError('');
    setSaving(true);
    try {
      const config: VideoQuizConfig = {
        type: 'video_quiz',
        source,
        videoUrl: videoUrl.trim(),
        title: uploadName || undefined,
        questions: questions.map((q) => ({ ...q, question: q.question.trim() })),
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_VIDEO_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save to slide.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center text-base">🎬</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Interactive Video</h2>
          <p className="text-[10px] text-slate-500">Timestamped questions you push during the video</p>
        </div>
      </div>

      {/* Source toggle */}
      <div className="flex gap-2">
        <button onClick={() => setSource('youtube')}
          className={clsx('flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-colors',
            source === 'youtube' ? 'bg-red-500/10 border-red-500/40 text-red-600' : 'border-surface-border text-slate-500')}>
          <Youtube size={14} /> YouTube
        </button>
        <button onClick={() => setSource('upload')}
          className={clsx('flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-colors',
            source === 'upload' ? 'bg-brand-500/10 border-brand-500/40 text-brand-600' : 'border-surface-border text-slate-500')}>
          <Upload size={14} /> Upload
        </button>
      </div>

      {source === 'youtube' ? (
        <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="Paste a YouTube link (https://youtube.com/watch?v=…)"
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-red-500" />
      ) : (
        <div className="space-y-2">
          <label className="flex items-center justify-center gap-2 w-full bg-surface-elevated border border-dashed border-surface-border rounded-xl px-3 py-3 text-xs text-slate-500 cursor-pointer hover:border-brand-500/40">
            <Upload size={14} /> {uploadName || 'Choose a video file'}
            <input type="file" accept="video/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setUploadName(f.name); setVideoUrl(f.name); } }} />
          </label>
          <p className="text-[10px] text-slate-500">
            You'll play this file yourself during class. AI generation is available for YouTube links; add questions manually for uploads.
          </p>
        </div>
      )}

      {/* AI generate (YouTube only) */}
      {source === 'youtube' && (
        <div className="flex items-center gap-2">
          <select value={genCount} onChange={(e) => setGenCount(Number(e.target.value))}
            className="bg-surface-elevated border border-surface-border rounded-lg px-2 py-2 text-xs text-slate-900">
            {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} Qs</option>)}
          </select>
          <Button fullWidth onClick={handleGenerate} loading={generating} disabled={!videoUrl.trim()}
            icon={<Sparkles size={14} />}>
            {generating ? 'Analyzing video…' : 'Generate with AI'}
          </Button>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
      )}

      {/* Question list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Questions ({questions.length})
          </p>
          <button onClick={() => setQuestions((p) => [...p, blankMC()])}
            className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700">
            <Plus size={12} /> Add
          </button>
        </div>

        <AnimatePresence>
          {questions.map((q, qi) => (
            <motion.div key={q.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-surface-elevated border border-surface-border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-slate-500 w-5">{qi + 1}.</span>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Clock size={12} />
                  <input type="number" min={0} value={q.timestamp}
                    onChange={(e) => updateQ(q.id, { timestamp: Math.max(0, Number(e.target.value)) })}
                    className="w-14 bg-surface border border-surface-border rounded px-1.5 py-1 text-xs text-slate-900 text-center" />
                  <span className="text-[10px] tabular-nums">{fmt(q.timestamp)}</span>
                </div>
                <div className="flex-1" />
                <button onClick={() => updateQ(q.id, q.kind === 'mc'
                  ? { kind: 'short', options: undefined }
                  : { kind: 'mc', options: blankMC().options })}
                  className="text-[10px] px-2 py-1 rounded-lg bg-surface border border-surface-border text-slate-600">
                  {q.kind === 'mc' ? 'Multiple Choice' : 'Short Answer'}
                </button>
                <button onClick={() => removeQ(q.id)} className="p-1 text-slate-500 hover:text-red-500"><Trash2 size={13} /></button>
              </div>

              <textarea value={q.question} onChange={(e) => updateQ(q.id, { question: e.target.value })}
                placeholder="Question…" rows={2}
                className="w-full bg-surface border border-surface-border rounded-lg px-2.5 py-2 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-brand-500" />

              {q.kind === 'mc' && (
                <div className="space-y-1.5">
                  {(q.options ?? []).map((o: MCOption, oi) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <button onClick={() => setCorrect(q.id, o.id)}
                        className={clsx('w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                          o.isCorrect ? 'bg-green-500 border-green-400 text-white' : 'border-surface-border text-slate-500')}>
                        {o.isCorrect ? <Check size={11} /> : OPTION_LABELS[oi]}
                      </button>
                      <input type="text" value={o.text} onChange={(e) => updateOpt(q.id, o.id, e.target.value)}
                        placeholder={`Option ${OPTION_LABELS[oi]}`}
                        className="flex-1 bg-surface border border-surface-border rounded-lg px-2 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500" />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {questions.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">
            Generate questions with AI or add them manually.
          </p>
        )}
      </div>

      <Button fullWidth size="md" onClick={handleSave} loading={saving}
        disabled={!canSave || !isOfficeReady}
        variant={saved ? 'secondary' : 'primary'} icon={saved ? <Check size={14} /> : undefined}>
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
